import "./EngineData.css";
import { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";
import {
  Radio,
  Hash,
  MapPin,
  Settings,
  PowerOff,
  Navigation,
  Wifi,
  Settings2,
} from "lucide-react";
import { formatReadableDate } from "../../utils/formatDate";
const DataRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    {" "}
    {icon && <span className="flex-shrink-0">{icon}</span>}{" "}
    <span className="text-sm text-slate-400 flex-shrink-0">{label}</span>{" "}
    <span className="text-sm font-bold text-white ml-auto">{value}</span>{" "}
  </div>
);

export default function Telemetry({ data }) {
  // const { id, data.response. } = data || {};

  const gpsStatus =
    data?.response?.LAT !== 0 && data?.response?.LON !== 0 ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-400 border border-green-400 rounded">
        Good
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-yellow-400 border border-yellow-400 rounded">
        No Fix. Map Default
      </span>
    );

  // const controllerType =
  //   data.response?.R42487 === 1
  //     ? `${data.CTRL_TYPE || ""}PLG`
  //     : data.CTRL_TYPE || "N/A";
  // console.log(data);

  // ✅ Track high temperature warning
  const [highTempWarning, setHighTempWarning] = useState(null);
  const [modbusWarning, setModbusWarning] = useState(null);
  useEffect(() => {
    const tailTemp = data?.response?.TAILSHAFT_TEMP;
    if (tailTemp > 224) {
      setHighTempWarning(`⚠️ High Tailshaft Temperature: ${tailTemp} °F`);
    } else {
      setHighTempWarning(null);
    }
  }, [data?.response?.TAILSHAFT_TEMP]);

  useEffect(() => {
    const fetchData = async () => {
      if (!data?.unitId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/device-registers/${data?.unitId}/1`
        );
        // console.log(
        //   "response last invalid data",
        //   response.data.response[0].time
        // );
        // console.log("response valid update", data.time);

        if (
          response.data.response.length > 0 &&
          data.time <= response.data.response[0].time &&
          response.data.response[0].type === "MODBUS"
        ) {
          setModbusWarning(
            `Last Valid Update : ${formatReadableDate(
              new Date(data.time * 1000)
            )} ⚠️ Modbus Error`
          );
        }
        if (!response)
          throw new Error(`HTTP error! status: ${response.status}`);
      } catch (err) {
        console.error("Error fetching engine data:", err);
      } finally {
      }
    };
    fetchData();
  }, [data?.unitId, data?.time]);

  async function fetchPlungerDescription(sdValue, plungerName) {
    if (!sdValue || sdValue <= 1) return "";
    const description = await fetch(
      `${process.env.REACT_APP_API_GATEAWAY}/eventParties/getDescription?companyId=COMPANY-2020-06-06-03-15-14-925872&sd=${sdValue}&controller=${data?.type}`
    )
      .then((res) => res.json())
      .then((d) => d.description);
    return `${plungerName} Shutdown : ${description}`;
  }

  async function setPlungerDescription(data) {
    const plungerValues = [
      { sd: data?.response?.R40797, name: "Plunger 1" },
      { sd: data?.response?.R40798, name: "Plunger 2" },
      { sd: data?.response?.R40799, name: "Plunger 3" },
      { sd: data?.response?.R40800, name: "Plunger 4" },
    ];

    let lastShutdown = "";

    for (const plunger of plungerValues) {
      if (plunger.sd > 1) {
        lastShutdown = await fetchPlungerDescription(plunger.sd, plunger.name);
      }
    }

    return lastShutdown; // will return only the last plunger that is shutdown
  }
  const [plungerDesc, setPlungerDesc] = useState("");

  useEffect(() => {
    async function fetchDesc() {
      const desc = await setPlungerDescription(data);

      setPlungerDesc(desc);
    }
    fetchDesc();
  }, [
    data?.response?.R40797,
    data?.response?.R40798,
    data?.response?.R40799,
    data?.response?.R40800,
  ]);

  return (
    <div className="mt-2">
      {" "}
      {highTempWarning && (
        <div className="bg-red-800 text-white text-sm font-semibold p-2  flex items-center justify-center ">
          {highTempWarning}
        </div>
      )}
      {plungerDesc && (
        <div className="bg-red-800 text-white text-sm font-semibold p-2  flex items-center justify-center ">
          {plungerDesc}
        </div>
      )}
      {modbusWarning && (
        <div className="bg-orange-500 text-white text-sm font-semibold p-2  flex items-center justify-center ">
          {modbusWarning}
        </div>
      )}
      <div className="flex-1 bg-slate-800 p-3 ">
        {/* Header */}

        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700 ">
          <Radio className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm font-semibold text-white">Telemetry</h3>
        </div>

        {/* Data Rows */}
        <div className="space-y-3">
          <DataRow
            icon={<Settings2 className="w-4 h-4 text-grey-400" />}
            label="Last Valid Update"
            value={
              data?.date
                ? formatReadableDate(new Date(data.time * 1000))
                : "N/A"
            }
          />
          <DataRow
            icon={<Hash className="w-4 h-4 text-cyan-400" />}
            label="ID"
            value={data?.unitId || "N/A"}
          />
          <DataRow
            icon={<MapPin className="w-4 h-4 text-green-400" />}
            label="GPS"
            value={gpsStatus}
          />
          <DataRow
            icon={<Settings className="w-4 h-4 text-yellow-400" />}
            label="Controller Type"
            value={
              (data?.type === "C5E" || data?.type === "C5") &&
              ((data?.response?.R42487 ?? 0) === 1 ||
                (data?.response?.R42488 ?? 0) === 1 ||
                (data?.response?.R42489 ?? 0) === 1 ||
                (data?.response?.R42490 ?? 0) === 1)
                ? `${data.type}PLG`
                : data?.type || "N/A"
            }
          />

          <DataRow
            icon={<PowerOff className="w-4 h-4 text-purple-400" />}
            label="Shutdown Enum"
            value={data?.response?.SHUTDOWN_ENUM || 0}
          />
          <DataRow
            icon={<Navigation className="w-4 h-4 text-grey-400" />}
            label="Latitude"
            value={data?.response?.LAT || 0}
          />
          <DataRow
            icon={<Navigation className="w-4 h-4 text-green-400" />}
            label="Longitude"
            value={data?.response?.LON || 0}
          />
          <DataRow
            icon={<Wifi className="w-4 h-4 text-cyan-400" />}
            label="Network"
            value={data?.ntw || "N/A"}
          />
        </div>
      </div>
    </div>
  );
}
